import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import {
  FORM_FIELD_CLASS_NAMES,
  FORM_FIELD_NAMES,
  MAX_REVIEW_TITLE_LENGTH,
  MAX_REVIEW_TEXT_LENGTH,
  MAX_REVIEW_AUTHOR_NAME_LENGTH,
  FIELD_DANGER_CLASS_NAMES,
} from 'site-modules/dealerships/constants/dealer-review';
import { RatingStarsInput } from 'site-modules/shared/components/rating-stars-input/rating-stars-input';
import { REVIEWS_SALES } from 'client/constants/dealer-reviews';
import { DealerReviewOptionField } from './dealer-review-option-field';
import { DealerReviewEmailField } from './dealer-review-email-field';
import { DealerReviewSubmitButton } from './dealer-review-submit-button';
import { DealerReviewDisclaimer } from './dealer-review-disclaimer';

export class DealerReviewFormFields extends Component {
  static propTypes = {
    fieldRef: PropTypes.func.isRequired,
    validate: PropTypes.func.isRequired,
    validationErrors: PropTypes.shape({}),
    reviewType: PropTypes.string.isRequired,
    showOptionErrors: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    isUpdate: PropTypes.bool,
  };

  static defaultProps = {
    validationErrors: {},
    showOptionErrors: false,
    isLoading: false,
    isUpdate: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      remainingTitleCharacters: MAX_REVIEW_TITLE_LENGTH,
      remainingTextCharacters: MAX_REVIEW_TEXT_LENGTH,
      remainingAuthorNameCharacters: MAX_REVIEW_AUTHOR_NAME_LENGTH,
    };
  }

  onReviewTitleChange = (event, fieldName) => {
    this.props.validate(event, fieldName);

    this.setState({
      remainingTitleCharacters: MAX_REVIEW_TITLE_LENGTH - event.target.value.length,
    });
  };

  onReviewTextChange = (event, fieldName) => {
    this.props.validate(event, fieldName);

    this.setState({
      remainingTextCharacters: MAX_REVIEW_TEXT_LENGTH - event.target.value.length,
    });
  };
  onReviewAuthorNameChange = (event, fieldName) => {
    this.props.validate(event, fieldName);

    this.setState({
      remainingAuthorNameCharacters: MAX_REVIEW_AUTHOR_NAME_LENGTH - event.target.value.length,
    });
  };

  onSubmit = event => {
    this.props.onSubmit(event, this.state);
  };

  /**
   * Handles option select.
   * @param {String} value
   * @param {String} name
   */
  selectOption = ({ value, name }) => {
    this.setState({ [name]: value });
  };

  render() {
    const { validate, validationErrors, reviewType, fieldRef, showOptionErrors, isLoading, isUpdate } = this.props;
    const {
      remainingTitleCharacters,
      remainingTextCharacters,
      remainingAuthorNameCharacters,
      recommendValue,
      purchaseValue,
      satisfiedValue,
      completeTheWorkValue,
      commitmentsValue,
      quoteOfTheWorkValue,
      fairPriceValue,
    } = this.state;
    const { USER_RATING, TITLE, TEXT, AUTHOR_NAME } = FORM_FIELD_NAMES;
    const serviceOptions = [
      {
        legendTitle: 'Were you satisfied with the quality of work performed on your vehicle?*',
        name: 'satisfiedValue',
        value: satisfiedValue,
      },
      {
        legendTitle: 'Did the dealership complete the work within the timeframe promised?*',
        name: 'completeTheWorkValue',
        value: completeTheWorkValue,
      },
      {
        legendTitle: 'Did the dealer honor all commitments made?*',
        name: 'commitmentsValue',
        value: commitmentsValue,
      },
      {
        legendTitle: 'Did the dealership provide you with an accurate quote of the work to be performed?*',
        name: 'quoteOfTheWorkValue',
        value: quoteOfTheWorkValue,
      },
      {
        legendTitle: 'Do you feel the price paid for service(s) was fair and appropriate?*',
        name: 'fairPriceValue',
        value: fairPriceValue,
      },
    ];
    const isUserRatingError = USER_RATING in validationErrors;
    const isTitleError = TITLE in validationErrors;
    const isTextError = TEXT in validationErrors;
    const isAuthorNameError = AUTHOR_NAME in validationErrors;

    return (
      <Fragment>
        <FormField
          {...FORM_FIELD_CLASS_NAMES}
          ref={fieldRef}
          tag={RatingStarsInput}
          label={isUpdate ? 'New overall rating*' : 'Overall rating*'}
          name={USER_RATING}
          id={USER_RATING}
          onChange={validate}
          onBlur={validate}
          isValid={!isUserRatingError}
          fieldSetAndLegend
        />
        <div className={classnames(FIELD_DANGER_CLASS_NAMES, { 'mb-1_5': isUserRatingError })}>
          {validationErrors[USER_RATING]}
        </div>

        <FormField
          {...FORM_FIELD_CLASS_NAMES}
          ref={fieldRef}
          name={TITLE}
          id={TITLE}
          label={isUpdate ? 'New review title*' : 'Review title*'}
          placeholder="Give your review a catchy title!"
          maxLength={MAX_REVIEW_TITLE_LENGTH}
          onChange={this.onReviewTitleChange}
          onBlur={validate}
          isValid={!isTitleError}
          inputClassName="py-0_5"
        />
        <div className="field-danger-wrapper d-flex justify-content-between">
          <div className={FIELD_DANGER_CLASS_NAMES}>{validationErrors[TITLE]}</div>
          <div className="small text-gray-dark pt-0_25 mb-1_5">Character limit: {remainingTitleCharacters}</div>
        </div>

        <label className={classnames(FORM_FIELD_CLASS_NAMES.labelClassName)} htmlFor={TEXT}>
          {isUpdate ? 'New ' : 'Your '} review*
        </label>
        <p className="text-gray-darker mb-0_5">
          Describe your likes and/or dislikes. Do not type in ALL CAPS; do not include HTML or links, profanity or
          name-calling, or personal contact information.
        </p>
        <FormField
          {...FORM_FIELD_CLASS_NAMES}
          tag="textarea"
          rows={7}
          ref={fieldRef}
          name={TEXT}
          id={TEXT}
          placeholder="What should other shoppers know about this dealership?"
          onChange={this.onReviewTextChange}
          onBlur={validate}
          isValid={!isTextError}
          inputClassName="py-0_5"
          maxLength={MAX_REVIEW_TEXT_LENGTH}
        />
        <div className="field-danger-wrapper d-flex justify-content-between">
          <div className={FIELD_DANGER_CLASS_NAMES}>{validationErrors[TEXT]}</div>
          <div className="small text-gray-dark pt-0_25 mb-1_5">Character limit: {remainingTextCharacters}</div>
        </div>
        <DealerReviewOptionField
          legendTitle="Would you recommend this dealership to friends and family?*"
          name="recommendValue"
          value={recommendValue}
          onOptionSelect={this.selectOption}
          showOptionErrors={showOptionErrors}
        />
        {reviewType === REVIEWS_SALES ? (
          <Fragment>
            <DealerReviewOptionField
              legendTitle="Did you purchase a vehicle from this dealer?*"
              name="purchaseValue"
              value={purchaseValue}
              onOptionSelect={this.selectOption}
              showOptionErrors={showOptionErrors}
            />
          </Fragment>
        ) : (
          <Fragment>
            {serviceOptions.map(({ legendTitle, name, value }) => (
              <DealerReviewOptionField
                key={name}
                legendTitle={legendTitle}
                name={name}
                value={value}
                onOptionSelect={this.selectOption}
                showOptionErrors={showOptionErrors}
              />
            ))}
          </Fragment>
        )}
        {!isUpdate && (
          <Fragment>
            <label className={classnames(FORM_FIELD_CLASS_NAMES.labelClassName)} htmlFor={AUTHOR_NAME}>
              Author Name or Nickname* <span className="fw-normal">(will be published with review)</span>
            </label>
            <p className="mb-0_5">Only letters, numbers, and spaces will be accepted.</p>
            <FormField
              {...FORM_FIELD_CLASS_NAMES}
              ref={fieldRef}
              name={AUTHOR_NAME}
              id={AUTHOR_NAME}
              placeholder="Example: John Doe"
              maxLength={MAX_REVIEW_AUTHOR_NAME_LENGTH}
              onBlur={validate}
              onChange={this.onReviewAuthorNameChange}
              isValid={!isAuthorNameError}
              inputClassName="py-0_5"
            />
            <div className="field-danger-wrapper d-flex justify-content-between">
              <div className={FIELD_DANGER_CLASS_NAMES}>{validationErrors[AUTHOR_NAME]}</div>
              <div className="small text-gray-dark pt-0_25 mb-1_5">
                Character limit: {remainingAuthorNameCharacters}
              </div>
            </div>
            <DealerReviewEmailField fieldRef={fieldRef} validationErrors={validationErrors} validate={validate} />
          </Fragment>
        )}
        <DealerReviewDisclaimer isUpdate={isUpdate} />
        <DealerReviewSubmitButton onClick={this.onSubmit} isLoading={isLoading}>
          Submit {reviewType} review
        </DealerReviewSubmitButton>
      </Fragment>
    );
  }
}
